import { hexToRgbString } from '@utils/Format'
import { isOverdueWithStatuses, isParentTask } from '@modules/tarefas/utils/isOverdue'
import { taskStatusId } from '@modules/tarefas/utils/optimisticStatusMove'
import {
  TASK_STATUS_CATEGORY,
  TASK_STATUS_CATEGORY_LABELS,
  TASK_STATUS_CATEGORY_COLORS,
  TASK_STATUS_CATEGORY_HEX,
  FINAL_STATUS_CATEGORIES,
} from '@modules/tarefas/constants/Text'
import {
  buildTaskMatchPredicate,
  pruneTaskTree,
} from '@modules/tarefas/utils/taskTreeFilter'
import {
  isAnyTaskFilterActive,
  taskFilterSignature as buildTaskFilterSignature,
} from '@modules/tarefas/utils/filterQuery'
import { _statusMapById, _loadedStatuses, _taskCategory } from './getters-internal'
import { sortByOrder, applySort, buildClientNameMap } from './getters-sort'

// Universo de raízes antes da poda: as tarefas da sprint ativa, ou o
// `state.tasks` inteiro em modo visão rápida (cross-pasta). Devolve a MESMA
// referência de `state.tasks` quando não há sprint ativa — é o primeiro elo do
// contrato de identidade (R1).
// Lista de DESTINO de uma tarefa espelhada (`mirroredInto.sprintId`), ou null.
// O `sprintId` do próprio nó continua sendo o da lista de ORIGEM — espelhar não
// move a tarefa.
function _mirrorTargetSprintId(task) {
  if (!task || task.isMirrored !== true) return null
  const destino = task.mirroredInto
  return destino && destino.sprintId != null ? destino.sprintId : null
}

// Primeiro funil da Lista/Kanban. A tarefa ESPELHADA pertence à lista ativa pelo
// espelhamento, não pelo próprio `sprintId` — que é o da origem. Sem o ramo de
// espelho ela era descartada aqui, ANTES do agrupamento por status: vinha no
// payload e nunca chegava à tela.
function _sprintBase(state) {
  const tasks = Array.isArray(state.tasks) ? state.tasks : []
  if (!state.activeSprintId) return tasks
  return tasks.filter(function (t) {
    if (t && t.isMirrored === true) {
      return _mirrorTargetSprintId(t) === state.activeSprintId
    }
    return t.sprintId === state.activeSprintId
  })
}

// Resultado completo da poda recursiva — `{ tree, contextIds, matchedCount }`.
//
// Substitui os dois filtros PLANOS anteriores (`_applySearchFilter` +
// `_applyStatusFilter`), que descartavam uma sub-tarefa correspondente sempre
// que o pai dela não correspondia. A regra de retenção
// (`manter(n) = casa(n) ∨ ∃c: manter(c)`) tem UMA implementação e ela vive em
// `utils/taskTreeFilter.js` (ADR-2) — aqui só a consumimos.
//
// Os três getters públicos abaixo derivam TODOS de `taskFilterResult`, que é um
// getter Vuex — logo a poda roda UMA vez por invalidação, não três.
//
// Antes disto `_taskFilterResult` era uma função pura chamada pelos três: o Vuex
// memoiza cada getter separadamente, mas nenhum deles compartilhava o resultado,
// então cada mudança em `tasks`/`searchQuery`/`statusFilter` disparava três
// travessias completas da árvore. Em modo visão rápida a árvore é o escritório
// inteiro (não há `activeSprintId` para reduzir a base), e o payload medido em
// produção chegou a 5,14 MB — três travessias ali são visíveis na digitação.
//
// Por que um getter e NÃO um memo manual por identidade de array: uma mutação
// in-place num nó (`task.title = x`, reativa e válida em Vue 2) não troca a
// referência de `state.tasks`. Um memo keyed por identidade serviria resultado
// velho; o getter do Vuex invalida porque o Vue rastreia as PROPRIEDADES lidas
// durante a avaliação. A memoização correta aqui é a do framework.
function _taskFilterResult(state) {
  return pruneTaskTree(_sprintBase(state), buildTaskMatchPredicate(state))
}

// Resultado completo da poda — `{ tree, contextIds, matchedCount }`.
// Fonte única dos três getters abaixo.
export function taskFilterResult(state) {
  return _taskFilterResult(state)
}

// Lê o resultado JÁ memoizado quando o Vuex injeta `getters`. A chamada direta
// sem `getters` (getters invocados como função pura nos testes unitários)
// recomputa — mesmo valor, sem o compartilhamento.
function _resolveFilterResult(state, getters) {
  if (getters && getters.taskFilterResult) return getters.taskFilterResult
  return _taskFilterResult(state)
}

// Árvore podada. Sem NENHUM filtro ativo o predicado é `null` e a árvore volta
// por identidade — a mesma referência de array e os mesmos nós (AC-6.5,
// AC-10.1). Default `[]`, nunca `undefined`.
export function currentSprintTasks(state, getters) {
  return _resolveFilterResult(state, getters).tree
}

// Agenda (calendário) — MESMO par predicate/prune que `currentSprintTasks`
// (`buildTaskMatchPredicate` + `pruneTaskTree`, `utils/taskTreeFilter.js`),
// aplicado sobre `state.agendaTasks` em vez de `_sprintBase(state)`.
//
// ★ Não reusa `_resolveFilterResult`/`taskFilterResult` (o getter memoizado
// de `currentSprintTasks`) de propósito: aquele getter é keyed pela sprint
// ativa do board (`_sprintBase`), e a Agenda busca a PRÓPRIA janela de datas
// via `loadAgendaTasks` — universos diferentes. Compartilhar o memo faria a
// poda da Agenda invalidar/recomputar junto com a do board sem necessidade,
// e um dos dois sempre estaria lendo o resultado errado.
//
// Contrato de identidade (R1) preservado: sem filtro ativo, devolve a MESMA
// referência de `state.agendaTasks`.
export function agendaTasksFiltered(state) {
  const tasks = Array.isArray(state.agendaTasks) ? state.agendaTasks : []
  return pruneTaskTree(tasks, buildTaskMatchPredicate(state)).tree
}

// True quando qualquer uma das 7 fontes de filtro está ativa (os 3 board
// filters, as 2 pontas de data, busca e status). Delega a
// `isAnyTaskFilterActive`, que COMPÕE `hasBoardFiltersActive` em vez de
// substituí-lo. Default `false`.
export function hasAnyTaskFilterActive(state) {
  return isAnyTaskFilterActive(state) === true
}

// Ids dos nós RETIDOS que não casam o predicado — os pais-contexto (AC-6.6).
// Consumido pelos componentes de linha via prop para renderizar o chip
// `Contexto`. Default `new Set()`.
export function taskFilterContextIds(state, getters) {
  return _resolveFilterResult(state, getters).contextIds
}

// Contagens da linha-resumo (UC-6): quantos nós casaram e quantos entraram só
// como contexto. Default `{ matched: 0, context: 0 }`.
export function taskFilterCounts(state, getters) {
  const result = _resolveFilterResult(state, getters)
  return { matched: result.matchedCount, context: result.contextIds.size }
}

// String estável do CONJUNTO de filtros ativos — token de reset da expansão
// derivada (AC-6.4). Estável sob reordenação de array. Default `''`.
export function taskFilterSignature(state) {
  return buildTaskFilterSignature(state)
}

// Erro da última carga da lista (AC-1.7). Default `null`.
export function tasksLoadError(state) {
  return state.tasksLoadError || null
}

export function searchQuery(state) {
  return typeof state.searchQuery === 'string' ? state.searchQuery : ''
}

export function statusFilter(state) {
  return Array.isArray(state.statusFilter) ? state.statusFilter : []
}

export function parentTasks(state) {
  const tasks = state.tasks || []
  return tasks.filter(isParentTask)
}

export function overdueTasks(state, getters) {
  const statusMap = _statusMapById(getters.activeFolderStatuses)
  return getters.currentSprintTasks.filter(function (task) {
    return isOverdueWithStatuses(task, statusMap)
  })
}

// Buckets the sprint tasks BY STATUS ID into a `{ [statusId]: [...] }` map,
// plus a derived `overdue` bucket. Data-driven on the active folder's
// statuses: each task is placed under its resolved `statusId`. A task in a
// `done`/`closed`-category status is never overdue (category-based
// suppression, AC-10). Tasks whose status no longer exists in the folder
// (archived + reassigned race) are dropped from the columns rather than
// crashing or creating an orphan column (AC-12). `overdue` collects tasks in
// non-final categories whose due date is past.
function bucketTasksByStatus(sprintTasks, statuses) {
  const statusMap = _statusMapById(statuses)
  const statusByCategory = _firstStatusByCategory(statuses)
  const buckets = { overdue: [] }
  for (const s of statuses) {
    if (s && s.id != null) buckets[s.id] = []
  }
  for (const task of sprintTasks) {
    const bucketKey = _statusBucketKey(task, statusMap, statusByCategory)
    if (bucketKey != null) buckets[bucketKey].push(task)
  }
  return buckets
}

// Primeiro status de cada categoria, na ordem em que a pasta os declara. É o
// destino de uma tarefa ESPELHADA: os status são por pasta, então o `statusId`
// dela pertence à pasta de ORIGEM e não casa nenhum balde daqui. Cair na coluna
// de categoria equivalente é o mesmo critério que `_remap_status_to_folder` usa
// no backend ao mover tarefa entre pastas.
function _firstStatusByCategory(statuses) {
  const map = {}
  if (!Array.isArray(statuses)) return map
  for (const s of statuses) {
    if (s && s.category != null && map[s.category] == null) map[s.category] = s
  }
  return map
}

// Resolves which bucket a task belongs to when grouping by status, or null for
// an orphan/unresolved status (skipped — no crash, no phantom column, AC-12).
// Non-final tasks past their due date collapse into the shared `overdue`
// bucket; everything else lands in its own status column.
function _statusBucketKey(task, statusMap, statusByCategory) {
  const sid = task && taskStatusId(task)
  let status = sid == null ? null : statusMap[sid]
  // Espelhada: o `statusId` é da pasta de ORIGEM e nunca resolve aqui. Sem este
  // fallback ela some da lista de destino em silêncio — era exatamente o bug em
  // que espelhar dava sucesso e a tarefa não aparecia. `statusCategory` vem do
  // backend (`_inject_status_meta`), o mesmo campo que a visão rápida usa para
  // agrupar cross-pasta.
  if (!status && task && task.isMirrored === true && statusByCategory) {
    status = statusByCategory[task.statusCategory] || null
  }
  if (!status) return null
  const isFinal = !!FINAL_STATUS_CATEGORIES[status.category]
  if (isFinal || !isOverdueWithStatuses(task, statusMap)) return status.id
  return 'overdue'
}

export { sortComparator } from './getters-sort'


// Buckets the sprint tasks by CATEGORY into the legacy 5-key shape
// (`todo/doing/done/closed` + derived `overdue`) consumed by the List view.
// Category-driven (data-driven on the active folder's statuses) instead of
// the old hardcoded `task.status` literal: every custom `open` status feeds
// `todo`, every `in_progress` feeds `doing`, `done`→`done`, `closed`→`closed`.
// This keeps the List's 4 fixed groups working with any custom status set
// while honoring the category semantics (AC-10 overdue suppression).
function bucketTasksByCategory(sprintTasks, statuses) {
  const statusMap = _statusMapById(statuses)
  const buckets = { done: [], overdue: [], doing: [], todo: [], closed: [] }
  for (const task of sprintTasks) {
    const category = _taskCategory(task, statusMap)
    if (category === TASK_STATUS_CATEGORY.CLOSED) {
      buckets.closed.push(task)
    } else if (category === TASK_STATUS_CATEGORY.DONE) {
      buckets.done.push(task)
    } else if (isOverdueWithStatuses(task, statusMap)) {
      buckets.overdue.push(task)
    } else if (category === TASK_STATUS_CATEGORY.IN_PROGRESS) {
      buckets.doing.push(task)
    } else {
      // open category (or unresolved) → A Fazer bucket (default).
      buckets.todo.push(task)
    }
  }
  return buckets
}

export function tasksByStatus(state, getters) {
  const statuses = getters.activeFolderStatuses
  const buckets = bucketTasksByCategory(getters.currentSprintTasks, statuses)
  const chain = state && state.sortChain
  const ctx = {
    clientNameById: buildClientNameMap(state),
    statusMap: _statusMapById(statuses),
  }
  return {
    done: applySort(buckets.done, chain, ctx),
    overdue: applySort(buckets.overdue, chain, ctx),
    doing: applySort(buckets.doing, chain, ctx),
    todo: applySort(buckets.todo, chain, ctx),
    closed: applySort(buckets.closed, chain, ctx),
  }
}

// The ordered multi-column chain (`[{ field, direction }]`). Empty = Manual.
export function sortChain(state) {
  return (state && Array.isArray(state.sortChain) && state.sortChain) || []
}

// Back-compat: the PRIMARY column as a legacy `{ field, direction }` object,
// or the null-primary default when the chain is empty. Consumers that only
// care about the first sort key (e.g. the toolbar control) read this.
export function sort(state) {
  const chain = (state && Array.isArray(state.sortChain) && state.sortChain) || []
  const primary = chain[0]
  if (primary && primary.field != null) {
    return {
      field: primary.field,
      direction: primary.direction === 'desc' ? 'desc' : 'asc',
    }
  }
  return { field: null, direction: 'asc' }
}

// Kanban columns are ALWAYS manual (`sortByOrder`, null-last) — they NEVER
// reflect the field-sort chain (decision DEC-3 / AC-S8). The List uses
// `tasksByStatus` (chain-aware); the Kanban uses this getter.
//
// Now DATA-DRIVEN on the active folder's statuses (customizar-status-tarefas):
// one column per active status in `position` order, each carrying its real
// `statusId`/`color`/`category`. The derived "Atrasadas" column is inserted
// per DEC-3 — between the last `in_progress`-category column and the first
// `done`-category column (matching the legacy todo|doing|overdue|done order).
// `closed`-category columns render only when "Mostrar fechadas" is ON (AC-9).
// Maps a single status to its Kanban column descriptor (real status column).
function _statusColumn(status, buckets) {
  return {
    id: status.id,
    label: status.name,
    color: status.color,
    category: status.category,
    statusId: status.id,
    data: sortByOrder(buckets[status.id] || []),
  }
}

// Builds the Kanban columns from the folder statuses + bucketed tasks. The
// derived "Atrasadas" column is inserted before the first done/closed-category
// column (DEC-3); when no final column exists it is appended at the end.
// Closed-category columns render only when `includeClosed` is true (AC-9).
function _buildKanbanColumns(statuses, buckets, includeClosed, overdueColumn) {
  const columns = []
  let overdueInserted = false
  for (const status of statuses) {
    if (status.category === TASK_STATUS_CATEGORY.CLOSED && !includeClosed) continue
    const isFinalCol = !!FINAL_STATUS_CATEGORIES[status.category]
    if (!overdueInserted && isFinalCol) {
      columns.push(overdueColumn)
      overdueInserted = true
    }
    columns.push(_statusColumn(status, buckets))
  }
  if (!overdueInserted) columns.push(overdueColumn)
  return columns
}

export function kanbanColumnsManual(state, getters) {
  const statuses = (getters.activeFolderStatuses || []).filter(function (s) {
    return s && s.active !== false
  })
  const buckets = bucketTasksByStatus(getters.currentSprintTasks, statuses)
  const overdueColumn = {
    id: 'overdue',
    label: 'Atrasadas',
    derived: true,
    category: 'overdue',
    data: sortByOrder(buckets.overdue || []),
  }
  return _buildKanbanColumns(statuses, buckets, state.includeClosed === true, overdueColumn)
}

// Legacy alias kept for any other consumer; delegates to the manual columns
// so the Kanban path can never reflect the chain.
export function kanbanColumns(state, getters) {
  return getters.kanbanColumnsManual
}

// --- List groups (data-driven, chain-aware) ----------------------------
// "Atrasadas" is a derived (non-status) group — fixed danger red.
const _OVERDUE_GROUP_RGB = '235, 87, 87'

// Quick-view (cross-folder) category axis in render order. `closed` is appended
// conditionally by the builder (only when `state.includeClosed === true`).
const _QUICK_VIEW_CATEGORY_ORDER = [
  TASK_STATUS_CATEGORY.OPEN,
  TASK_STATUS_CATEGORY.IN_PROGRESS,
  TASK_STATUS_CATEGORY.DONE,
]

// Maps a category to its `bucketTasksByCategory` bucket key (the buckets keep
// the legacy 5-key shape `todo/doing/done/closed` + derived `overdue`).
const _CATEGORY_TO_BUCKET = {
  [TASK_STATUS_CATEGORY.OPEN]: 'todo',
  [TASK_STATUS_CATEGORY.IN_PROGRESS]: 'doing',
  [TASK_STATUS_CATEGORY.DONE]: 'done',
  [TASK_STATUS_CATEGORY.CLOSED]: 'closed',
}

// Builds the List groups for QUICK-VIEW (cross-folder) mode. There is no active
// folder, so grouping by a single folder's statuses is impossible (that map is
// empty → every task would be dropped). Instead we group by the FIXED category
// taxonomy shared across folders, resolving each task's category from the
// embedded `statusCategory` (via `bucketTasksByCategory` → `_taskCategory`).
// Order: "Atrasadas" first, then open → in_progress → done, and closed only
// when `includeClosed`. Groups carry `statusId: null` / `createStatus: null` —
// inline-create and status drag/drop are suppressed in quick-view (the
// list-view guards them with `v-if="!isQuickViewMode"`), so a null status is
// safe here. `rgb` is derived from the category hex (`TASK_STATUS_CATEGORY_HEX`)
// so `task-list-view` renders a valid `rgb(r, g, b)` dot.
function _quickViewListGroups(categoryBuckets, includeClosed, chain, ctx) {
  const groups = []
  groups.push({
    key: 'overdue',
    statusId: null,
    label: 'Atrasadas',
    color: 'var(--danger)',
    rgb: _OVERDUE_GROUP_RGB,
    category: 'overdue',
    tasks: applySort(categoryBuckets.overdue || [], chain, ctx),
    createStatus: null,
  })
  const order = _QUICK_VIEW_CATEGORY_ORDER.slice()
  if (includeClosed === true) order.push(TASK_STATUS_CATEGORY.CLOSED)
  for (const category of order) {
    const bucketKey = _CATEGORY_TO_BUCKET[category]
    groups.push({
      key: category,
      statusId: null,
      label: TASK_STATUS_CATEGORY_LABELS[category] || '',
      color: TASK_STATUS_CATEGORY_COLORS[category] || 'secondary',
      rgb: hexToRgbString(TASK_STATUS_CATEGORY_HEX[category]),
      category: category,
      tasks: applySort(categoryBuckets[bucketKey] || [], chain, ctx),
      createStatus: null,
    })
  }
  return groups
}

// Monta os grupos da List em modo visão rápida (cross-pasta). A categoria de
// cada tarefa é resolvida contra TODAS as fatias de status carregadas — não só
// a da pasta ativa, que aqui é vazia por definição.
//
// Isso é o que faz a linha pular de grupo depois de alterar o status: a
// resposta do PUT (`get_task_detail`) NÃO traz `statusCategory`, e a mutation
// UPDATE_TASK substitui a tarefa-raiz (perde o campo embutido) ou mescla a
// subtarefa (preserva o valor ANTIGO). Resolver por `statusId` é sempre fresco;
// o campo embutido segue como fallback para pastas ainda não carregadas
// (ver `_taskCategory`).
function _quickViewGroups(state, getters, chain, clientNameById) {
  const statuses = _loadedStatuses(state).filter(function (s) {
    return s && s.active !== false
  })
  const ctx = { clientNameById: clientNameById, statusMap: _statusMapById(statuses) }
  const categoryBuckets = bucketTasksByCategory(getters.currentSprintTasks, statuses)
  return _quickViewListGroups(categoryBuckets, state.includeClosed, chain, ctx)
}

// Builds the List view groups from the active folder's statuses. Mirrors the
// Kanban's data-driven columns (one group per active status, position order)
// but is CHAIN-AWARE: each bucket is ordered via `applySort` honoring the sort
// chain (the Kanban is always manual). UX/order decision (confirmed by the
// reference screenshot): "Atrasadas" comes FIRST, then every active status in
// `position` order — this DIFFERS from the Kanban, which inserts "Atrasadas"
// right before the first final column. `closed`-category statuses render only
// when `state.includeClosed === true` (same rule as the Kanban, AC-9), keeping
// their natural position order at the end.
export function listGroups(state, getters) {
  const statuses = (getters.activeFolderStatuses || []).filter(function (s) {
    return s && s.active !== false
  })
  const chain = state && state.sortChain
  const ctx = {
    clientNameById: buildClientNameMap(state),
    statusMap: _statusMapById(statuses),
  }
  // Quick-view (cross-folder): no active folder → the folder status map is
  // empty and status bucketing would drop every task. Group by the fixed
  // category taxonomy instead (see `_quickViewGroups`).
  if (getters.isQuickViewMode) {
    return _quickViewGroups(state, getters, chain, ctx.clientNameById)
  }
  const buckets = bucketTasksByStatus(getters.currentSprintTasks, statuses)
  const groups = []
  // "Atrasadas" first (derived bucket). `createStatus` is the folder default
  // status id so inline-create in this group still lands on a real status
  // (AC-15) instead of the non-existent "overdue" status.
  const defaultStatus = getters.defaultStatusForFolder(state.activeFolderId)
  groups.push({
    key: 'overdue',
    statusId: null,
    label: 'Atrasadas',
    color: 'var(--danger)',
    rgb: _OVERDUE_GROUP_RGB,
    category: 'overdue',
    tasks: applySort(buckets.overdue || [], chain, ctx),
    createStatus: defaultStatus ? defaultStatus.id : null,
  })
  // One group per active status, in position order. Closed only when toggled.
  for (const status of statuses) {
    if (status.category === TASK_STATUS_CATEGORY.CLOSED && state.includeClosed !== true) continue
    groups.push({
      key: status.id,
      statusId: status.id,
      label: status.name,
      color: status.color,
      rgb: hexToRgbString(status.color),
      category: status.category,
      tasks: applySort(buckets[status.id] || [], chain, ctx),
      createStatus: status.id,
    })
  }
  return groups
}

export function overviewStats(state) {
  return state.overviewStats
}
